import { ReadonlyVec2, ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';
import { isPointInsidePolygon } from '~/engine/util/algorithms/sdf/polygonWinding';
import { DashedGhostPreviewRenderer } from '~/engine/view/DashedGhostPreviewRenderer';
import { PlaneView } from '~/engine/view/views/PlaneView';

/**
 * Per-frame data required to draw a single suggestion ghost.
 *
 * `polygonsVoxel` are in the texture grid's voxel-centered
 * continuous coordinate system (the output of marching squares).
 * `targetVoxelZ` is the Z voxel coordinate of the target slice in
 * the same grid. `textureGrid` is used to convert each polygon
 * vertex from voxel space to patient (world) space.
 *
 * `roiColor` is the structure color in [0,1] floats.
 */
export type ContourAutoSuggestionPreviewState = {
    polygonsVoxel: ReadonlyArray<ReadonlyArray<vec2>>;
    targetVoxelZ: number;
    textureGrid: Grid;
    roiColor: ReadonlyVec3;
};

/** The outline of `state` in patient coordinates (mm), one loop per polygon. */
export function previewOutlineMm(
    state: ContourAutoSuggestionPreviewState
): vec3[][] {
    return state.polygonsVoxel.map((polygon) =>
        polygon.map((vertex) =>
            state.textureGrid.toWorldSpace(
                vec3.fromValues(vertex[0], vertex[1], state.targetVoxelZ + 0.5)
            )
        )
    );
}

/** A closed loop of points on a canvas, in CSS px from its top-left corner. */
export type CanvasLoop = ReadonlyArray<ReadonlyVec2>;

/** A rectangle on a canvas, in CSS px from its top-left corner. */
export interface CanvasRectPx {
    left: number;
    top: number;
    width: number;
    height: number;
}

/** Whether a segment of the closed loop touches the rect (slab clipping). */
export function loopReachesRect(loop: CanvasLoop, rect: CanvasRectPx): boolean {
    const low = [rect.left, rect.top];
    const high = [rect.left + rect.width, rect.top + rect.height];
    return loop.some((to, index) => {
        const from = loop[index === 0 ? loop.length - 1 : index - 1];
        let enter = 0;
        let exit = 1;
        for (const axis of [0, 1]) {
            const start = from[axis];
            const delta = to[axis] - start;
            if (delta === 0) {
                if (start < low[axis] || start > high[axis]) {
                    return false;
                }
                continue;
            }
            const atLow = (low[axis] - start) / delta;
            const atHigh = (high[axis] - start) / delta;
            enter = Math.max(enter, Math.min(atLow, atHigh));
            exit = Math.min(exit, Math.max(atLow, atHigh));
            if (enter > exit) {
                return false;
            }
        }
        return true;
    });
}

/**
 * Whether any of the loops shows on a canvas of this size: one of its segments
 * reaches the canvas, or, with no line on it, the loop encloses its centre.
 */
export function loopsShowOnCanvas(
    loops: ReadonlyArray<CanvasLoop>,
    width: number,
    height: number
): boolean {
    const canvas = { left: 0, top: 0, width, height };
    return (
        width > 0 &&
        height > 0 &&
        loops.some(
            (loop) =>
                loop.length >= 2 &&
                (loopReachesRect(loop, canvas) ||
                    isPointInsidePolygon([width / 2, height / 2], loop))
        )
    );
}

/** Whether the outline, in patient mm, shows on the canvas of `view`. */
export function outlineShowsOn(
    view: PlaneView,
    outlineMm: ReadonlyArray<ReadonlyArray<ReadonlyVec3>>
): boolean {
    const [width, height] = view.size;
    return loopsShowOnCanvas(
        outlineMm.map((loop) =>
            loop.map((point) => view.canvasCoordsFromPatientCoords(point))
        ),
        width,
        height
    );
}

/**
 * The auto-suggestion face of {@link DashedGhostPreviewRenderer}: maps the
 * marching-squares output (voxel space, one colour for the whole state)
 * onto the shared renderer's world-space, per-polygon-colour contract.
 * The conversion happens once per {@link setState} — the grid and target
 * slice are fixed for a given state, so a later {@link refresh} (pan/
 * zoom) re-projects the SAME world points rather than re-deriving them.
 */
export class ContourAutoSuggestionPreviewRenderer {
    private readonly inner: DashedGhostPreviewRenderer;

    public constructor(planeView: PlaneView) {
        this.inner = new DashedGhostPreviewRenderer(planeView);
    }

    public setState(state: ContourAutoSuggestionPreviewState): void {
        this.inner.setState({
            polygons: previewOutlineMm(state).map((points) => ({
                points,
                color: state.roiColor,
            })),
        });
    }

    /** Re-render with the current state (e.g. after a pan/zoom event). */
    public refresh(): void {
        this.inner.refresh();
    }

    public clear(): void {
        this.inner.clear();
    }

    public dispose(): void {
        this.inner.dispose();
    }
}
